import React, {useEffect, useRef, useState} from 'react'
import {NavLink, useLocation, useParams} from "react-router-dom";
import {cn} from "@/utils/shadcn-utils";
import {ChevronLeft, ChevronRight} from 'lucide-react';

type NavItemType = {
    link: string;
    value: string;
    icon?: string | React.ReactNode;
    isActive?: boolean;
}

type NavTabProps = {
    navItems: NavItemType[];
    baseRoute?: string;
    className?: string;
    mode?: "switch" | "underlined",
    indexPathname?: string
}

const NavTabs = ({navItems, baseRoute = "", mode = "underlined", indexPathname = "", className}: NavTabProps) => {
    const {id} = useParams<{ id: string }>();
    const {pathname} = useLocation();
    const pathNameWithSlash = pathname.at(-1) === "/"? pathname: pathname+ "/"
    const currentPath = location.pathname.split('/').pop();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const activeItems = navItems.filter(item => item.isActive || item.isActive === undefined);

    const checkScrollability = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const {scrollLeft, scrollWidth, clientWidth} = container;

        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    };

    const scroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const scrollAmount = 200;
        const newScrollLeft = direction === 'left'
            ? container.scrollLeft - scrollAmount
            : container.scrollLeft + scrollAmount;

        container.scrollTo({
            left: newScrollLeft,
            behavior: 'smooth'
        });
    };

    const isActive = (itemLink: string) => {
        if (baseRoute && !indexPathname) {
            const fullItemPath = itemLink === ""
                ? `${baseRoute.replace(":id", id)}`
                : `${baseRoute.replace(":id", id)}${itemLink}`;
            return itemLink === ""
                ? pathNameWithSlash === fullItemPath
                : pathNameWithSlash.startsWith(fullItemPath);

        }
        // console.log(9520, currentPath === itemLink || indexPathname)
        return currentPath === itemLink || indexPathname;
    }

    useEffect(() => {
        checkScrollability();

        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => checkScrollability();
        const handleResize = () => checkScrollability();

        container.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        return () => {
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    if (mode === "switch") {
        return (
            <div className="w-full my-4">
                <div className="relative">
                    {showLeftArrow && (
                        <button
                            onClick={() => scroll('left')}
                            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full p-1 shadow-sm transition-all duration-200"
                            aria-label="Scroll left"
                        >
                            <ChevronLeft size={25}/>
                        </button>
                    )}

                    {showRightArrow && (
                        <button
                            onClick={() => scroll('right')}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full p-1 shadow-sm transition-all duration-200"
                            aria-label="Scroll right"
                        >
                            <ChevronRight size={25}/>
                        </button>
                    )}

                    <nav
                        ref={scrollContainerRef}
                        className="bg-[#E5E7EB] text-[#64748B] p-1 rounded-md overflow-x-auto scrollbar-none"
                        style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}
                    >
                        <div className="flex gap-1 items-center" style={{width: 'max-content'}}>
                            {activeItems.map((item) => {
                                return (
                                    <NavLink
                                        key={item.link}
                                        to={item.link}
                                        className={`p-2 px-3 rounded-md flex items-center gap-1.5 transition-all duration-200 ${
                                            isActive(item.link)
                                                ? "bg-white font-semibold text-gray-900"
                                                : "hover:bg-gray-200/50"
                                        }`}
                                        style={{
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            minWidth: 'max-content'
                                        }}
                                    >
                                        {item.icon && typeof item.icon === "string"
                                            ? <i className={item.icon}/>
                                            : item.icon
                                        }
                                        <span style={{whiteSpace: 'nowrap'}}>{item.value}</span>
                                    </NavLink>
                                )
                            })}
                        </div>
                    </nav>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full">
            <div className="relative justify-between w-full overflow-y-auto">
                {showLeftArrow && (
                    <button
                        onClick={() => scroll('left')}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full p-1 shadow-sm transition-all duration-200"
                        aria-label="Scroll left"
                    >
                        <ChevronLeft size={16}/>
                    </button>
                )}

                {showRightArrow && (
                    <button
                        onClick={() => scroll('right')}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full p-1 shadow-sm transition-all duration-200"
                        aria-label="Scroll right"
                    >
                        <ChevronRight size={16}/>
                    </button>
                )}

                <div
                    ref={scrollContainerRef}
                    className="overflow-x-auto scrollbar-none border-b border-gray-200"
                    style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}
                >
                    <div className={cn("w-full flex gap-4 px-3", className)}>
                        {activeItems.map((item, index) => {
                            const fullItemPath = item.link === ""
                                ? `${baseRoute.replace(":id", id)}`
                                : `${baseRoute.replace(":id", id)}${item.link}`;

                            const isActive =
                              item.link === ""
                                ? pathNameWithSlash === fullItemPath
                                : pathNameWithSlash.startsWith(fullItemPath);
                            return (
                                <NavLink
                                    key={index}
                                    to={item.link}
                                    className={`flex items-center gap-1.5 pt-3 pb-2 px-1 transition-all duration-200 font-semibold border-b-2 ${
                                        isActive
                                            ? "border-blue-500 text-blue-500"
                                            : "border-b-transparent hover:text-gray-800 hover:border-gray-300"
                                    }`}
                                    style={{
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        minWidth: 'max-content'
                                    }}
                                >
                                    {item.icon && typeof item.icon === "string"
                                        ? <i className={item.icon}/>
                                        : item.icon
                                    }
                                    <span style={{whiteSpace: 'nowrap'}}>{item.value}</span>
                                </NavLink>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default NavTabs